/**
 * Ambient Sound Service
 * Streams MP3 files from the Supabase "ambient-audio" public bucket.
 * Supports looping with 500ms fade-in and fade-out.
 */



const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

// Public bucket — construct URLs directly
function getAmbientUrl(filename) {
  return `${SUPABASE_URL}/storage/v1/object/public/ambient-audio/${filename}`;
}

export const AMBIENT_TRACKS = [
  {
    id: 'forest_wind',
    filename: '1_forest_and_wind.mp3',
    label: 'Forest & Wind',
    description: 'Rustling leaves and gentle breeze',
    icon: 'Tree',
    color: 'emerald',
    activeColor: 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300',
    checkColor: 'text-emerald-400',
  },
  {
    id: 'by_the_lake',
    filename: '2_by_the_lake.mp3',
    label: 'By the Lake',
    description: 'Still waters and soft ripples',
    icon: 'Waves',
    color: 'sky',
    activeColor: 'bg-sky-500/20 border-sky-500/60 text-sky-300',
    checkColor: 'text-sky-400',
  },
  {
    id: 'by_the_river',
    filename: '3_by_the_river.mp3',
    label: 'By the River',
    description: 'Flowing stream over stones',
    icon: 'Drop',
    color: 'blue',
    activeColor: 'bg-blue-500/20 border-blue-500/60 text-blue-300',
    checkColor: 'text-blue-400',
  },
  {
    id: 'wind_chimes_rain',
    filename: '4_wind_chimes_rain.mp3',
    label: 'Wind Chimes & Rain',
    description: 'Gentle rain with chimes',
    icon: 'CloudRain',
    color: 'indigo',
    activeColor: 'bg-indigo-500/20 border-indigo-500/60 text-indigo-300',
    checkColor: 'text-indigo-400',
  },
  {
    id: 'by_the_ocean',
    filename: '5_by_the_ocean.mp3',
    label: 'By the Ocean',
    description: 'Rolling waves on the shore',
    icon: 'Waves',
    color: 'cyan',
    activeColor: 'bg-cyan-500/20 border-cyan-500/60 text-cyan-300',
    checkColor: 'text-cyan-400',
  },
  {
    id: 'early_morning_nature',
    filename: '6_early_morning_nature.mp3',
    label: 'Early Morning',
    description: 'Birdsong at dawn',
    icon: 'Sun',
    color: 'amber',
    activeColor: 'bg-amber-500/20 border-amber-500/60 text-amber-300',
    checkColor: 'text-amber-400',
  },
  {
    id: 'waterfall',
    filename: '7_waterfall.mp3',
    label: 'Waterfall',
    description: 'Cascading water and mist',
    icon: 'ArrowDown',
    color: 'teal',
    activeColor: 'bg-teal-500/20 border-teal-500/60 text-teal-300',
    checkColor: 'text-teal-400',
  },
];

const FADE_DURATION = 0.5; // seconds (500ms)

// ── Internal state ──────────────────────────────────────────────────────────
let audioEl = null;
let currentTrackId = null;
let targetVolume = 0.4;
let fadeTimer = null;
let _running = false;

function clearFadeTimer() {
  if (fadeTimer) {
    clearInterval(fadeTimer);
    fadeTimer = null;
  }
}

function fadeIn(audio, toVolume) {
  clearFadeTimer();
  audio.volume = 0;
  const steps = 20;
  const stepTime = (FADE_DURATION * 1000) / steps;
  const stepSize = toVolume / steps;
  let step = 0;
  fadeTimer = setInterval(() => {
    step++;
    audio.volume = Math.min(toVolume, stepSize * step);
    if (step >= steps) {
      audio.volume = toVolume;
      clearFadeTimer();
    }
  }, stepTime);
}

function fadeOut(audio, onComplete) {
  clearFadeTimer();
  const startVolume = audio?.volume;
  const steps = 20;
  const stepTime = (FADE_DURATION * 1000) / steps;
  const stepSize = startVolume / steps;
  let step = 0;
  fadeTimer = setInterval(() => {
    step++;
    audio.volume = Math.max(0, startVolume - stepSize * step);
    if (step >= steps) {
      audio.volume = 0;
      clearFadeTimer();
      if (onComplete) onComplete();
    }
  }, stepTime);
}

function createAudioElement(url, volume) {
  const el = new Audio(url);
  el.loop = true;
  el.volume = 0;
  el.preload = 'auto';
  return el;
}

// ── Public API ──────────────────────────────────────────────────────────────

export function startAmbient(trackId, volume = 0.4) {
  targetVolume = volume;

  const track = AMBIENT_TRACKS?.find(t => t?.id === trackId);
  if (!track) return;

  // Already playing the same track — just ensure volume is right
  if (_running && currentTrackId === trackId && audioEl) {
    clearFadeTimer();
    fadeIn(audioEl, targetVolume);
    return;
  }

  // Stop any existing audio first, then start new
  if (audioEl) {
    const oldEl = audioEl;
    audioEl = null;
    _running = false;
    fadeOut(oldEl, () => {
      oldEl?.pause();
      oldEl.src = '';
      _startNew(track, volume);
    });
  } else {
    _startNew(track, volume);
  }
}

function _startNew(track, volume) {
  const url = getAmbientUrl(track?.filename);
  const el = createAudioElement(url, volume);
  audioEl = el;
  currentTrackId = track?.id;
  _running = true;

  el?.play()?.then(() => {
    fadeIn(el, volume);
  })?.catch(() => {
    // Autoplay blocked — will start on next user interaction
    _running = false;
  });
}

export function stopAmbient(fade = true) {
  if (!audioEl) return;
  _running = false;
  const el = audioEl;
  audioEl = null;
  currentTrackId = null;

  if (fade) {
    fadeOut(el, () => {
      el?.pause();
      el.src = '';
    });
  } else {
    clearFadeTimer();
    el?.pause();
    el.src = '';
  }
}

export function pauseAmbient() {
  if (!audioEl || !_running) return;
  fadeOut(audioEl, () => {
    if (audioEl) audioEl?.pause();
  });
}

export function resumeAmbient() {
  if (!audioEl) return;
  _running = true;
  audioEl?.play()?.then(() => {
    fadeIn(audioEl, targetVolume);
  })?.catch(() => {});
}

export function setAmbientVolume(volume) {
  targetVolume = volume;
  if (audioEl && _running) {
    clearFadeTimer();
    audioEl.volume = volume;
  }
}

export function isAmbientRunning() {
  return _running && audioEl !== null;
}
